'use strict';

var _ = require('lodash');
var d3 = require('d3');
var RomSummaryLineGraphChart = function RomSummaryLineGraphChart(AbstractChart, chartGroups, chartXAxis, chartYAxis, chartBrush, chartConstants, chartCurve, d3Helper, chartCircles, chartPinLine, $timeout) {
  var chartParamsBuilder = require('./RomSummaryLineGraphParams');
  var chartScales = require('./RomSummaryLineGraphScales');
  var Chart = function Chart(container, formatters, config) {
    AbstractChart.call(this, container);
    var initialDataLength;
    // chart components

    var groups = chartGroups('dimension', undefined, true);
    var groupsPostData = chartGroups('dimension', formatters && formatters.tooltipFormatter, true);
    var brush, updateBrushSelection, maxSelectedWeeks;
    var curves = chartCurve('dimension', 'value', 'curveData').discontinuous(true);
    var preDataCurves = chartCurve('dimension', 'value', 'curveData').discontinuous(true);
    var xAxis = chartXAxis(formatters.xAxisTickFormatter);
    if (config && config.xAxisSkew) {
      xAxis.withTextRotation(chartConstants.AXIS.SKEW.DEG_30_B);
    }
    var circles = chartCircles('circles', 3);
    var xYearAxis;
    if (formatters.xYearAxisTickFormatter === undefined) {
      xYearAxis = false;
    } else {
      xYearAxis = chartXAxis(formatters.xYearAxisTickFormatter);
    }
    if (config && config.xAxisLabel) {
      d3Helper.createGroupElement(this.dataGroup, 'x-axis-label').append('text').text(config.xAxisLabel);
    }
    var yAxisLeft = chartYAxis(formatters.yAxisTickFormatter, chartConstants.AXIS.MODE.LEFT).label(config.yAxisLabel);
    var dispatch = d3.dispatch('rangechangeend', 'rangechange');
    d3.rebind(this, dispatch, 'on');
    this.brushChangeAction = function (selection) {
      if (selection.length !== 0) {
        var ranges = _.isArray(selection[0]) ? selection : [selection];
        var wideCurveData = _.transform(this.data, function (result, dataset) {
          var clone = _.clone(dataset);
          clone.curveData = _.clone(dataset.curveData);
          clone.curveData = _.map(dataset.curveData, function (element) {
            var r = _.clone(element);
            r.value = undefined;
            return r;
          });
          _.forEach(ranges, function (range) {
            for (var i = range[0]; i < range[1]; i++) {
              clone.curveData[i].value = dataset.curveData[i].value;
            }
          });
          result.push(clone);
        }, []);
        this.preDataGroup.datum(wideCurveData);
        this.callForPreDataGroup([preDataCurves.params(this.params)], this.scales);
        this.postDataGroup.call(groupsPostData.withRanges(ranges));
      }
    };
    if (config && config.brush) {
      brush = chartBrush();
      updateBrushSelection = function (range) {
        var selection = brush.getSelection();
        var selectionSize = selection[0][1] - selection[0][0];
        if (selectionSize === 0) {
          brush.setSelection(range);
        } else {
          this.brushChangeAction(selection);
        }
      }.bind(this);
      groupsPostData.on('groupclick', function (d, i) {
        brush.setSelection([i, i + 1]);
        dispatch.rangechangeend(brush.getSelection());
      });
      brush.on('brushend', function (range) {
        dispatch.rangechangeend(range);
      });
      brush.on('brushchange', function (range, triggerEnd) {
        this.brushChangeAction(range);
        dispatch.rangechange(brush.getSelection());
        if (triggerEnd) {
          dispatch.rangechangeend(brush.getSelection());
        }
      }.bind(this));
    }
    function calculateXYearAxisValue(values) {
      var result = [];
      for (var i = 0; i < values.length; i++) {
        var value = values[i];
        if (value.month() >= 6) {
          result.push(value);
          i += 52 - value.week() + 26;
        }
      }
      var lastValue = values[values.length - 1];
      if (lastValue.month() < 6) {
        result.push(lastValue);
      }
      if (result.length === 1) {
        result = [values[Math.round(values.length / 2) - 1]];
      }
      return result;
    }
    function prepareCirclesData(xValue, data) {
      return _.transform(data, function (result, dataset) {
        var element = _.find(dataset.curveData, {
          dimension: xValue
        });
        if (element.value !== undefined) {
          result.push({
            css: dataset.className,
            value: element.value
          });
        }
      }, []);
    }
    function prepareStackLabelData(xValue, data) {
      var element = _.find(data[0].curveData, {
        dimension: xValue
      });
      return element.stackLabel;
    }
    this.drawCircles = function () {
      var self = this;
      var wholeDomain = _.map(self.data[0].curveData, 'dimension');
      var circlesDataset = _.transform(wholeDomain, function (result, element) {
        result.push({
          dimension: element,
          circles: prepareCirclesData(element, self.data),
          stackLabel: prepareStackLabelData(element, self.data)
        });
      });
      this.postDataGroup.datum(circlesDataset);
      this.callForPostDataGroup([groupsPostData.params(this.params), circles.params(this.params)], this.scales);
    };
    this.render = function (data, range) {
      var chartParams = chartParamsBuilder(data, config && config.forceWidth);
      this.initChart(data, range, chartParams);
      initialDataLength = initialDataLength || data[0].curveData.length;
      this.scales = chartScales(this.params, this.data, range);
      var pinLine = chartPinLine();
      this.dataGroup.call(pinLine.scales(this.scales).chartParams(this.params).chartData(this.data).chartRange(range));
      this.callForDataGroup([groups, curves, xAxis], this.scales);

      // update pre-data group curves
      this.preDataGroup.call(curves.params(this.params).scales(this.scales));
      this.preDataGroup.call(yAxisLeft.params(this.params).scales(this.scales));
      if (xYearAxis !== false) {
        var xYearAxisValues = calculateXYearAxisValue(this.scales.xScale.domain());
        d3Helper.createGroupElement(this.dataGroup, 'x-axis-year').attr('transform', 'translate(0,30)').call(xYearAxis.withTickValues(xYearAxisValues).params(this.params).scales(this.scales));
      }
      if (config && config.xAxisLabel) {
        d3Helper.createGroupElement(this.dataGroup, 'x-axis-label').attr('transform', 'translate(' + -this.params.padding.left + ',' + (this.params.height + 20) + ')');
      }
      this.drawCircles();
      var groupsElement = this.postDataGroup.select('.groups')[0][0];
      this.postDataGroup.selectAll('circle').on('mouseover', function () {
        pinLine.show(d3.mouse(groupsElement)[0]);
      }).on('mouseout', function () {
        pinLine.hide();
      });
      if (config && config.brush) {
        this.preDataGroup.call(brush.withDomainRange(this.visibleXRange).scales(this.scales));
        updateBrushSelection(range);
      }
    };
    this.expandBrush = function () {
      var domainLength = initialDataLength;
      if (maxSelectedWeeks) {
        brush.setSelection([Math.max(0, domainLength - maxSelectedWeeks), domainLength]);
      } else {
        brush.setSelection([0, domainLength]);
      }
    };
    this.resetBrushSelection = function () {
      this.expandBrush();
      $timeout(function () {
        dispatch.rangechangeend(brush.getSelection());
      });
    };
    this.resetInitialDataLength = function () {
      initialDataLength = null;
    };
    this.clearBrush = function () {
      brush.getModel().clearRanges();
      brush.clear();
    };
    this.domainChange = function (range) {
      this.render(this.data, range);
    };
    this.withMaxSelectedWeeks = function (value) {
      maxSelectedWeeks = value;
      return this;
    };
    this.setInitialSelection = function (range) {
      brush.setSelection(range, true);
    };
    this.getBrush = function () {
      return brush;
    };
    container.on('$destroy', function () {
      dispatch.on('rangechangeend', null);
      dispatch.on('rangechange', null);
      if (config && config.brush) {
        groupsPostData.on('groupclick', null);
        brush.on('brushchange', null);
        brush.on('brushend', null);
      }
    });
  };
  Chart.prototype = Object.create(AbstractChart.prototype);
  Chart.constructor = Chart;
  return Chart;
};
RomSummaryLineGraphChart.$inject = ['AbstractChart', 'RomSummaryLineGraphGroup', 'ChartElementsXAxis', 'ChartElementsYAxis', 'ChartElementsMultiBrush', 'ChartElementsConstants', 'ChartElementsCurves', 'd3Helper', 'ChartElementsCircles', 'RomSummaryLineGraphPinLine', '$timeout'];
module.exports = RomSummaryLineGraphChart;